import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { match } from "ts-pattern";

import { useUser } from "@/web/auth/useUser";
import { Redirect } from "@/web/components/Redirect";
import { FullPageLoader } from "@/web/components/styled/FullPageLoader";
import { Spinner } from "@/web/components/primitives/spinner";
import { useFormNavigation } from "@/web/hooks/useFormNavigation";
import { ErrorHandler } from "@/web/lib/errors";
import { tableQueryOptions, useProcedureMutation } from "@/web/lib/tanstack";
import { procedures, tables } from "@tycho/web-bindings";
import type { SmbOnboardingRow } from "@tycho/web-bindings/types";
import { Button } from "@/web/components/primitives/button";
import { ClaimingNomination } from "@/web/routes/_protected/smb/-onboarding/ClaimingNomination";
import { ConfirmDetails } from "@/web/routes/_protected/smb/-onboarding/ConfirmDetails";
import { CustomizingStyle } from "@/web/routes/_protected/smb/-onboarding/CustomizingStyle";
import { IdentifyBusiness } from "@/web/routes/_protected/smb/-onboarding/IdentifyBusiness";
import { SmbSupplementalFormNew } from "@/web/routes/_protected/smb/-onboarding/SmbSupplementalFormNew";
import { SmbUserInfo } from "@/web/routes/_protected/smb/-onboarding/UserInfo";
import { SmbCreation } from "@/web/routes/_protected/smb/-SmbCreation";
import {
  getSmbOnboardingStep,
  invitedStepToWizardStep,
  isInvitedOnboarding,
  smbBackTarget,
  smbOnboardingFromViewRow,
  type SmbOnboarding,
  type SmbOnboardingStep,
} from "@/web/routes/_protected/smb/-types";

export const Route = createFileRoute("/_protected/onboarding/smb")({
  component: SmbOnboardingPage,
  // `?new=1` signals an existing brand-owner intentionally onboarding an
  // ADDITIONAL brand (the profile "Add brand" button links here). Without it, a
  // user who already has a brand is redirected to their dashboard.
  //
  // `?invite=<opaque-code>` signals an acquisition invitation: a WITH.CO admin
  // seeded an invited-brand draft and sends the owner this direct link. It
  // bypasses the existing-owner dashboard redirect and starts at card
  // customization. `?new=1` and `?invite=` are mutually exclusive.
  //
  // `?email=` rides along on invite links purely to pre-fill the sign-in field
  // (consumed by the `_protected` shell while signed out). It is NOT identity:
  // the server matches the Clerk-derived email against the draft before any
  // bind or claim. Stripped from the URL alongside the code once bound.
  validateSearch: (
    search: Record<string, unknown>,
  ): { new?: boolean; invite?: string; email?: string } => {
    const invite =
      typeof search.invite === "string" && search.invite.trim() !== ""
        ? search.invite.trim()
        : undefined;
    const email =
      typeof search.email === "string" && search.email.trim() !== ""
        ? search.email.trim()
        : undefined;
    const isNew = search.new === true || search.new === "1" || search.new === "true";
    return {
      // When `?invite=` is present it is authoritative; `?new=1` is ignored.
      new: invite === undefined ? isNew : undefined,
      invite,
      email,
    };
  },
});

function SmbOnboardingPage() {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-screen items-center justify-center">
          <Spinner className="size-10" />
        </div>
      }
    >
      <SmbOnboardingRoute />
    </Suspense>
  );
}

function SmbOnboardingRoute() {
  const user = useUser();
  const { new: isAddingBrand, invite } = Route.useSearch();

  const { data: smbData } = useSuspenseQuery(tableQueryOptions(tables.smbOnboarding));
  const rawRow = (smbData[0] ?? null) as SmbOnboardingRow | null;

  // Latch: once this route mount has seen an invite link (or holds an invited
  // draft in progress), it stays in invited mode even after the code is removed
  // from the URL for security. Same-route `replace` navigations preserve this
  // component, so the invited controller below keeps its in-memory code.
  const [invitedSession, setInvitedSession] = useState(false);
  useEffect(() => {
    if (invite !== undefined) setInvitedSession(true);
  }, [invite]);

  if (!user) {
    return null;
  }

  const smbOnboarding: SmbOnboarding | null = rawRow ? smbOnboardingFromViewRow(rawRow) : null;

  if (smbOnboarding && isInvitedOnboarding(smbOnboarding)) {
    return <InvitedOnboardingFlow smbOnboarding={smbOnboarding} initialPublicCode={invite ?? ""} />;
  }

  if (invite !== undefined || invitedSession) {
    return <InvitedOnboardingFlow smbOnboarding={smbOnboarding} initialPublicCode={invite ?? ""} />;
  }

  // A user who already owns a brand only stays on onboarding if they are
  // deliberately adding another (`?new=1`) or already have a draft in flight.
  // Otherwise send them to the dashboard. A brandless user always onboards.
  if (user.brandIds.length > 0 && !isAddingBrand && !rawRow) {
    return <Redirect to="/profile/smb" replace />;
  }

  const computedStep: SmbOnboardingStep = smbOnboarding
    ? getSmbOnboardingStep(smbOnboarding)
    : "identifyBusiness";

  if (!smbOnboarding) {
    return <SmbCreation />;
  }

  return <SmbOnboardingFlow smbOnboarding={smbOnboarding} computedStep={computedStep} />;
}

// ---------------------------------------------------------------------------
// Invited onboarding — single owner of bind → terminal / wizard.
// ---------------------------------------------------------------------------

type InvitedGateState = "binding" | "conflict" | "invalid" | "ready";

/**
 * Owns the whole invited interaction. On a fresh link it binds (begin-or-
 * resume); on resume it just reads the persisted draft. Keeps the opaque code
 * in memory (not the URL) for the completion step, removes it from the URL,
 * renders terminal states, and advances the wizard with persisted `invitedStep`.
 */
function InvitedOnboardingFlow({
  smbOnboarding,
  initialPublicCode,
}: {
  smbOnboarding: SmbOnboarding | null;
  initialPublicCode: string;
}) {
  const router = useRouter();
  // Capture the code ONCE for this invited session. Survives the URL cleanup
  // (same-route `replace`) and any parent re-render.
  const [publicCode] = useState(initialPublicCode);
  const { mutateAsync: begin } = useProcedureMutation(procedures.beginInvitedBrandOnboarding);
  const { mutateAsync: setInvitedStep } = useProcedureMutation(
    procedures.updateInvitedOnboardingStep,
  );

  const [state, setState] = useState<InvitedGateState>(publicCode ? "binding" : "ready");
  const firedRef = useRef(false);

  const clearInviteCode = useCallback(() => {
    void router.navigate({
      to: ".",
      // `email` goes with the code: it has done its job (pre-filling sign-in)
      // by the time we bind, and leaving an address in the URL would put it in
      // history, referrers, and analytics for the rest of the flow.
      search: (prev: Record<string, unknown>) => ({ ...prev, invite: undefined, email: undefined }),
      replace: true,
    });
  }, [router]);

  const advance = useCallback(
    (next: SmbOnboardingStep) => {
      const stepTag = invitedStepTagFor(next);
      if (stepTag) {
        void setInvitedStep({ invitedStep: stepTag }).catch((error: unknown) => {
          // Non-fatal: the wizard still advances; a later refresh resumes from
          // the last persisted step.
          ErrorHandler.logError(error);
        });
      }
    },
    [setInvitedStep],
  );

  const confirmed = smbOnboarding !== null && isInvitedOnboarding(smbOnboarding);

  useEffect(() => {
    if (!publicCode) return;
    if (firedRef.current) return;
    // Resume path: the invited draft already exists, nothing to bind.
    if (confirmed) {
      setState("ready");
      clearInviteCode();
      return;
    }
    firedRef.current = true;
    void begin({ publicCode })
      .then((res) => {
        if (res.status.tag === "Conflict") {
          setState("conflict");
        } else {
          setState("ready");
        }
        clearInviteCode();
      })
      .catch((error: unknown) => {
        ErrorHandler.logError(error);
        setState("invalid");
        clearInviteCode();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicCode, confirmed, begin, clearInviteCode]);

  if (state === "conflict") {
    return (
      <InvitedStateCard
        title="Another onboarding in progress"
        body="You already have a business onboarding in progress. Finish that one before claiming this invitation, or add a new brand from your dashboard."
      />
    );
  }

  if (state === "invalid") {
    return (
      <InvitedStateCard
        title="Invitation invalid"
        body="This invitation link is no longer valid. Please contact your WITH.CO contact for a new link."
      />
    );
  }

  // Awaiting bind or the transient draft arriving on the view.
  if (state === "binding" || !confirmed) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <FullPageLoader />
      </div>
    );
  }

  // Confirmed invited draft: render the wizard.
  const invited = smbOnboarding as SmbOnboarding;
  const step = invitedStepToWizardStep(invited.invitedStep);

  return (
    <SmbOnboardingFlow
      smbOnboarding={invited}
      computedStep={step}
      isInvited
      onInvitedAdvance={advance}
      invitedPublicCode={publicCode || undefined}
    />
  );
}

function invitedStepTagFor(step: SmbOnboardingStep) {
  switch (step) {
    case "supplementalInfo":
      return { tag: "Supplemental" as const };
    case "userInfo":
      return { tag: "UserInfo" as const };
    default:
      return undefined;
  }
}

/** Terminal state card for invalid / revoked / expired / wrong-account / conflict invites. */
function InvitedStateCard({ title, body }: { title: string; body: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <div className="flex max-w-125 flex-col items-center gap-4 text-center">
        <div className="withus-strong text-3xl">{title}</div>
        <p className="text-withus-dark-blue/70">{body}</p>
        <Button variant="outline">
          <a href="/">Back to WITH.CO</a>
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared flow
// ---------------------------------------------------------------------------

function SmbOnboardingFlow({
  smbOnboarding,
  computedStep,
  isInvited = false,
  onInvitedAdvance,
  invitedPublicCode,
}: {
  smbOnboarding: SmbOnboarding;
  computedStep: SmbOnboardingStep;
  isInvited?: boolean;
  onInvitedAdvance?: (next: SmbOnboardingStep) => void;
  invitedPublicCode?: string | undefined;
}) {
  const { stage, stack } = useFormNavigation(computedStep);

  function goBack() {
    if (stack.length > 1) stage.pop();
    else stage.replace(smbBackTarget(stage.current, smbOnboarding));
  }

  function advance(next: SmbOnboardingStep) {
    if (isInvited) onInvitedAdvance?.(next);
    stage.push(next);
  }

  return match(stage.current)
    .with("identifyBusiness", () => (
      <IdentifyBusiness onContinue={(nextStep) => advance(nextStep)} />
    ))
    .with("confirmBusiness", () => (
      <ConfirmDetails
        smbOnboarding={smbOnboarding}
        onBack={goBack}
        onContinue={(nextStep) => advance(nextStep)}
      />
    ))
    .with("claimNomination", () => (
      <ClaimingNomination onBack={goBack} onContinue={() => advance("customizeStyling")} />
    ))
    .with("customizeStyling", () => (
      <CustomizingStyle
        smbOnboarding={smbOnboarding}
        // Invited flow starts at customization with no prior step, so there is
        // nothing to go back to — hide the back button instead of a no-op.
        onBack={isInvited ? undefined : goBack}
        onContinue={() => advance("supplementalInfo")}
      />
    ))
    .with("supplementalInfo", () => (
      <SmbSupplementalFormNew
        smbOnboarding={smbOnboarding}
        onBack={goBack}
        onContinue={() => advance("userInfo")}
      />
    ))
    .with("userInfo", () => (
      <SmbUserInfo
        onBack={goBack}
        smbOnboarding={smbOnboarding}
        invitedPublicCode={isInvited ? invitedPublicCode : undefined}
      />
    ))
    .exhaustive();
}
